import React, { FC, useRef, useState } from 'react'

import clsx from 'clsx'
import { useClickAway } from 'react-use'

import {
  ProjectSearchNarrowDown,
  ProjectSearchReset,
  ProjectSearchStatus,
  Search,
} from 'src/components/molecules'
import { ProjectSearchCompassRole } from 'src/components/molecules/projects/ProjectSearchCompassRole'

import { useGetProjectStatuses, useProjectStatus } from 'src/hooks'
import { useGetCompassRoles } from 'src/hooks/useGetCompassRoles'
import { hasElementInId } from 'src/libs/helper'
import { AssignedProjectSearchContentEntity } from 'src/types'

import styles from './assignProjectSearch.module.scss'

type Props = {
  editButtonId: string
  cancelButtonId: string
  searchContent: AssignedProjectSearchContentEntity | null
  updateSearchContent: (searchContent: AssignedProjectSearchContentEntity | null) => void
  className?: string
}

export const AssignedProjectSearch: FC<Props> = ({
  editButtonId,
  cancelButtonId,
  searchContent,
  updateSearchContent,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { compassRoles, loading } = useGetCompassRoles()
  const { getHasArchived } = useProjectStatus()
  const { projectStatuses } = useGetProjectStatuses()

  const ref = useRef(null)
  useClickAway(ref, event => {
    const target = event.target as HTMLElement
    // このコンポーネントの外側をクリックした場合は閉じるが、編集ボタンやキャンセルボタンをクリックした場合は閉じない
    if (
      isOpen &&
      !(target && (hasElementInId(target, editButtonId) || hasElementInId(target, cancelButtonId)))
    ) {
      setIsOpen(false)
    }
  })

  const handleChangeSearchWord = (word: string) => {
    if (!searchContent) {
      const newSearchContent: AssignedProjectSearchContentEntity = { word }
      updateSearchContent(newSearchContent)
      return
    }

    const updatedSearchContent: AssignedProjectSearchContentEntity = { ...searchContent, word }
    updateSearchContent(updatedSearchContent)
  }

  const handleUpdateCompassRole = (updatedCompassRoles: string[]) => {
    if (!searchContent) {
      const newSearchContent: AssignedProjectSearchContentEntity = {
        selectedCompassRoles: updatedCompassRoles,
      }
      updateSearchContent(newSearchContent)
      return
    }

    const updatedSearchContent: AssignedProjectSearchContentEntity = {
      ...searchContent,
      selectedCompassRoles: updatedCompassRoles,
    }
    updateSearchContent(updatedSearchContent)
  }

  const handleUpdateStatuses = (updatedStatuses: string[]) => {
    if (!searchContent) {
      const newSearchContent: AssignedProjectSearchContentEntity = {
        selectedStatuses: updatedStatuses,
      }
      updateSearchContent(newSearchContent)
      return
    }

    const updatedSearchContent: AssignedProjectSearchContentEntity = {
      ...searchContent,
      selectedStatuses: updatedStatuses,
    }
    updateSearchContent(updatedSearchContent)
  }

  return (
    <div className={className}>
      <div className="tw-relative" ref={ref}>
        <Search
          value={searchContent?.word ?? ''}
          onChange={handleChangeSearchWord}
          onFocus={() => setIsOpen(true)}
          placeholder="プロジェクト名称で検索（複数可）"
          className="tw-relative tw-z-20 tw-w-[21.43rem]"
        />
        {isOpen && (
          <div
            className={clsx(
              'tw-absolute tw-bg-surface-primary tw-border-border-secondary',
              styles.wrapper
            )}
          >
            <div className="tw-full-justify">
              <ProjectSearchNarrowDown isSearchEnabled={!!searchContent} />
              <ProjectSearchReset
                disabled={!searchContent}
                onClick={() => updateSearchContent(null)}
              />
            </div>

            <div className="tw-max-h-[36.14rem] tw-overflow-y-auto">
              <ProjectSearchStatus
                selectedStatusNames={searchContent?.selectedStatuses ?? []}
                updateStatusNames={handleUpdateStatuses}
                hasArchived={getHasArchived(projectStatuses)}
                className="tw-mt-s2"
              />
              {!loading && (
                <ProjectSearchCompassRole
                  compassRoles={compassRoles}
                  selectedCompassRoles={searchContent?.selectedCompassRoles ?? []}
                  updateCompassRoles={handleUpdateCompassRole}
                  className="tw-mt-s2"
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
